// src/userFlow/aboutUs.js
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Leaf,
  ShieldCheck,
  Users,
  Truck,
  Recycle,
  Globe,
} from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Slim header to keep branding consistent */}
      <header className="bg-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
            SodaSips
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-sm">
            <Link to="/" className="text-gray-700 hover:text-orange-500">Home</Link>
            <Link to="/about" className="text-gray-900 font-semibold">About</Link>
            <Link to="/contact" className="text-gray-700 hover:text-orange-500">Contact</Link>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-white to-red-100 opacity-60" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-sm font-semibold text-orange-600 tracking-wide uppercase">About Us</p>
                <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
                  Fizzy favorites from around the world—curated with care.
                </h1>
                <p className="mt-4 text-lg text-gray-600">
                  SodaSips started with a simple idea: make it fun and effortless to discover
                  amazing sodas—classic, craft, and everything in between—while offering inclusive
                  choices like sugar-free, vegan, gluten-free, and all-natural.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-orange-600 text-white hover:bg-orange-700 font-semibold"
                  >
                    <Sparkles className="w-4 h-4" />
                    Shop Featured Sodas
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-5 py-3 rounded-xl border border-gray-300 text-gray-800 hover:bg-gray-50 font-semibold"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden shadow-xl ring-1 ring-gray-200">
                  <img
                    className="w-full h-full object-cover"
                    src="https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?q=80&w=1440&auto=format&fit=crop"
                    alt="Assorted craft sodas on ice"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Values */}
        <section className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900">What drives us</h2>
            <p className="text-gray-600 mt-2">
              We’re building a storefront we’d love to shop at—transparent, fast, and inclusive.
            </p>

            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <Sparkles className="w-5 h-5" />,
                  title: 'Taste First',
                  body: 'Every product in our catalog is selected for flavor and quality—no filler.',
                },
                {
                  icon: <Leaf className="w-5 h-5" />,
                  title: 'Inclusive Options',
                  body: 'Sugar-free, vegan, gluten-free, and all-natural filters make it easy to shop your way.',
                },
                {
                  icon: <ShieldCheck className="w-5 h-5" />,
                  title: 'Trusted Sourcing',
                  body: 'We partner with reputable brands and small bottlers alike, with clear labeling.',
                },
                {
                  icon: <Users className="w-5 h-5" />,
                  title: 'Community',
                  body: 'We listen to feedback and continually expand flavors based on what you love.',
                },
                {
                  icon: <Truck className="w-5 h-5" />,
                  title: 'Fast, Careful Shipping',
                  body: 'Smart packing + quick turnarounds so your order arrives fresh and fizzy.',
                },
                {
                  icon: <Recycle className="w-5 h-5" />,
                  title: 'Better Footprint',
                  body: 'We prioritize recyclable materials and efficient shipping where possible.',
                },
              ].map((card) => (
                <div key={card.title} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-orange-50 text-orange-600">
                    {card.icon}
                  </div>
                  <h3 className="mt-4 font-semibold text-gray-900">{card.title}</h3>
                  <p className="mt-2 text-sm text-gray-600">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story / timeline */}
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-orange-600" />
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Our story</h2>
              </div>

              <ol className="mt-6 relative border-l border-gray-200 pl-6 space-y-6">
                <li>
                  <div className="absolute -left-2 top-1.5 w-3 h-3 rounded-full bg-orange-600" />
                  <h4 className="font-semibold text-gray-900">The spark</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    We started SodaSips after hunting down rare flavors for friends—realizing how hard it was to
                    find great choices in one place.
                  </p>
                </li>
                <li>
                  <div className="absolute -left-2 top-[4.25rem] w-3 h-3 rounded-full bg-orange-600" />
                  <h4 className="font-semibold text-gray-900">Building the catalog</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    We curated classics and craft bottles, added robust search, and introduced flavor filters.
                  </p>
                </li>
                <li>
                  <div className="absolute -left-2 top-[8.75rem] w-3 h-3 rounded-full bg-orange-600" />
                  <h4 className="font-semibold text-gray-900">Inclusive filters</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    Today you can filter by sugar-free, vegan, gluten-free, and all-natural—so everyone can sip happy.
                  </p>
                </li>
              </ol>

              <div className="mt-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50 text-sm font-semibold"
                >
                  Have a flavor suggestion? Tell us!
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl p-8 md:p-10 bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg">
              <h3 className="text-2xl font-bold">Questions? Collaborations? Press?</h3>
              <p className="mt-2 text-white/90">
                We’d love to hear from you—brands, creators, and soda superfans alike.
              </p>
              <div className="mt-5">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-5 py-3 rounded-xl bg-white text-gray-900 font-semibold hover:bg-white/90"
                >
                  Get in touch
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer — same structure as HomePage */}
      <footer className="bg-gray-800 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-4">
                SodaSips
              </div>
              <p className="text-gray-400">
                Your one-stop shop for the world's best sodas and beverages. Fresh, fizzy, and delivered to your door.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-orange-400 transition-colors duration-200">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-orange-400 transition-colors duration-200">Contact</Link></li>
                <li><Link to="/returns" className="hover:text-orange-400 transition-colors duration-200">Returns</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Connect</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://facebook.com" className="hover:text-orange-400 transition-colors duration-200">Facebook</a></li>
                <li><a href="https://twitter.com" className="hover:text-orange-400 transition-colors duration-200">Twitter</a></li>
                <li><a href="https://instagram.com" className="hover:text-orange-400 transition-colors duration-200">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SodaSips. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
